import { Link } from 'react-router-dom';
import { useDocumentTitle } from '../lib/useDocumentTitle';

export default function SafetyScreen() {
  useDocumentTitle('Safety guide');
  return (
    <div className="min-h-dvh safe-top safe-bottom container-app pb-12">
      <header className="flex items-center gap-3 pt-4 pb-2">
        <Link
          to="/"
          aria-label="Back"
          className="rounded-full inline-flex items-center justify-center"
          style={{ minWidth: 44, minHeight: 44, color: 'var(--color-primary)' }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </Link>
        <h1 className="text-xl font-semibold" style={{ letterSpacing: '-0.01em' }}>
          Safety guide
        </h1>
      </header>

      <div
        role="alert"
        style={{
          marginTop: 8,
          marginBottom: 24,
          padding: '14px 16px',
          borderRadius: 10,
          border: '2px solid var(--color-danger)',
          background: 'rgba(196,51,43,0.06)',
          color: 'var(--color-primary)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: 'var(--color-danger)',
            fontSize: 11,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontWeight: 700,
            marginBottom: 8,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          Read this if you have a severe allergy
        </div>
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55 }}>
          Menu to Menu is a tool to help you communicate. It's not a doctor, not a chef, not a substitute for paying attention.
          For severe or life-threatening allergies, the app reduces communication friction — it does not reduce medical risk on its own.
        </p>
      </div>

      <Section title="If a reaction would land you in the hospital">
        <ol style={{ paddingLeft: 18, margin: 0, lineHeight: 1.7 }}>
          <li>Tag dishes as <strong>Strict / medical</strong>, not Preference.</li>
          <li>Show the source-language phrase to the waiter AND ask them to read it back.</li>
          <li>Have the waiter walk it to the kitchen and return with confirmation.</li>
          <li>Carry a translated allergy card or doctor's note in the local language as backup.</li>
          <li>Carry your medication (epinephrine, antihistamines, etc.) regardless.</li>
        </ol>
      </Section>

      <Section title="Strict / medical vs Preference">
        <p>
          <strong>Preference</strong> is a polite question — <em>"Is this dish vegan?"</em> Yellow chip. The waiter may answer casually, even guess.
        </p>
        <p>
          <strong>Strict / medical</strong> prefixes your tag with allergy framing — <em>"I have a severe nut allergy. Please confirm with the kitchen this dish has no nuts, no peanut oil, and is not cooked alongside nut dishes."</em> Red card. Designed to escalate to the kitchen.
        </p>
        <p style={{ fontWeight: 600 }}>
          Always pick Strict / medical for actual allergies. Preference is for taste choices.
        </p>
      </Section>

      <Section title="Cross-contamination is real">
        <p>
          Shared oil, pans, cutting boards, and prep surfaces all carry trace amounts. The app has a dedicated <strong>"No cross-contamination"</strong> strict tag — use it for celiac disease, severe nut allergies, severe shellfish allergies, severe dairy allergies. The kitchen card explicitly asks for separate prep surfaces and clean cookware.
        </p>
      </Section>

      <Section title="Hidden ingredients are common">
        <p>Lard in beans. Fish sauce in stir-fries. Stock cubes in vegetable soup. Whey powder in bread. Gelatin in desserts. Anchovies in dressing. Almost never on the menu.</p>
        <p>Use the strict tag for:</p>
        <ul style={{ paddingLeft: 18 }}>
          <li><strong>No pork lard</strong> — Mexican beans, refried dishes, Latin American stews.</li>
          <li><strong>No fish sauce</strong> — Southeast Asian stir-fries, curries, soups.</li>
          <li><strong>No alcohol</strong> — sauces, marinades, slow-cooked dishes.</li>
        </ul>
      </Section>

      <Section title="Absence of a flag isn't a guarantee of absence">
        <p>
          The app does <strong>not</strong> mark dishes "safe." It hides dishes flagged as containing ingredients you've blocked when it's confident they're present. Allergens may be present but unlabeled, or unknown to the menu reader. Treat unflagged dishes the same way you'd treat any unfamiliar dish abroad — verify before eating.
        </p>
      </Section>

      <Section title="If your source language isn't fully covered yet">
        <p>
          Some safety phrases are still being reviewed by native speakers. When yours isn't yet finalized, the app shows the <strong>English version with a clear warning</strong> — <em>"Shown in English — [language] translation coming soon."</em>
        </p>
        <p>In that case:</p>
        <ul style={{ paddingLeft: 18 }}>
          <li>Pair the English phrase with the red allergy banner (which IS in the source language).</li>
          <li>Point at the dish on your screen.</li>
          <li>Find a colleague who reads English.</li>
          <li><strong>If you are not confident the message is getting through, do not order the dish.</strong></li>
        </ul>
        <p style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
          <strong>Native coverage today:</strong> English, Spanish, French, Italian, German, Portuguese, Japanese, Chinese (Simplified and Traditional), Korean, Thai, Vietnamese.
          <br />
          <strong>English fallback today:</strong> Russian, Polish, Turkish, Greek, Arabic, Hebrew, Persian, Hindi, Dutch, Indonesian, Czech, Ukrainian, Malay, Filipino, Swedish, Danish, Norwegian, Finnish.
        </p>
      </Section>

      <Section title="If your waiter doesn't read the source language either">
        <ul style={{ paddingLeft: 18 }}>
          <li>Find a colleague or manager who reads the menu's source language.</li>
          <li>Ask them to translate your card to the chef.</li>
          <li>If no one in the restaurant can read your safety phrase, <strong>leave</strong>.</li>
        </ul>
        <p>A restaurant where no one can confirm what's in their food is not safe for a strict allergy.</p>
      </Section>

      <div
        style={{
          marginTop: 24,
          padding: '18px 18px',
          borderRadius: 10,
          background: 'var(--color-surface)',
        }}
      >
        <p style={{ fontWeight: 700, fontSize: 15, margin: '0 0 10px' }}>Final word</p>
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: 'var(--color-primary)' }}>
          Pick the right severity. Confirm with the kitchen, not just the waiter. Carry your medication. Trust your gut — if something feels off about how the message is being received, don't eat the dish.
        </p>
        <p style={{ margin: '14px 0 0', fontSize: 14, lineHeight: 1.65, color: 'var(--color-primary)' }}>
          A second meal at a safer restaurant beats a hospital trip every time.
        </p>
      </div>

      <section style={{ paddingTop: 12, paddingBottom: 8 }}>
        <h2 className="text-base font-semibold" style={{ letterSpacing: '-0.01em', marginBottom: 8 }}>
          Per-condition guides
        </h2>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[
            { slug: 'celiac', label: 'Celiac safety guide' },
            { slug: 'halal', label: 'Halal travel guide' },
            { slug: 'kosher', label: 'Kosher travel guide' },
            { slug: 'vegan', label: 'Vegan travel guide' },
          ].map((g) => (
            <li key={g.slug}>
              <Link
                to={`/safety/${g.slug}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '14px 16px',
                  borderRadius: 8,
                  background: 'var(--color-surface)',
                  color: 'var(--color-primary)',
                  textDecoration: 'none',
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                <span>{g.label}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--color-text-secondary)' }}>
                  <polyline points="9 6 15 12 9 18" />
                </svg>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <p style={{ marginTop: 24, textAlign: 'center' }}>
        <Link
          to="/faq"
          style={{
            display: 'inline-block',
            padding: '10px 18px',
            borderRadius: 9999,
            border: '1px solid var(--color-divider)',
            color: 'var(--color-primary)',
            textDecoration: 'none',
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          Read the full FAQ →
        </Link>
      </p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ paddingBottom: 24 }}>
      <h2
        className="text-base font-semibold"
        style={{ letterSpacing: '-0.01em', marginBottom: 8 }}
      >
        {title}
      </h2>
      <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
        {children}
      </div>
    </section>
  );
}
